import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { SiteHeader } from '@/components/SiteHeader';
import { SiteFooter } from '@/components/SiteFooter';
import { ConcentricRings } from '@/components/Motifs';
import api from '@/lib/api';
import { usePageTitle } from '@/hooks/usePageTitle';

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.1, duration: 0.7, ease: [0.22, 1, 0.36, 1] }
  })
};

export default function AboutPage() {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  usePageTitle();

  useEffect(() => {
    api.get('/pages/about').then(res => {
      setPageData(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const sections = pageData?.sections || {};
  const founder = sections.founder || {};

  if (loading) {
    return (
      <div className="min-h-screen">
        <SiteHeader />
        <div className="flex justify-center py-32"><ConcentricRings size={60} opacity={0.3} /></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <SiteHeader />

      <section className="py-16 sm:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={{ visible: { transition: { staggerChildren: 0.12 } } }}
          >
            <motion.p
              variants={fadeUp}
              className="font-['Inter'] text-xs font-medium uppercase tracking-[0.14em] text-[hsl(var(--silver))] mb-4"
            >
              About
            </motion.p>
            <motion.h1
              variants={fadeUp}
              className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-bold text-[hsl(var(--foreground))] mb-8 leading-[1.1]"
            >
              {sections.headline || 'The Architect Behind the Observatory'}
            </motion.h1>

            <motion.p
              variants={fadeUp}
              className="font-['Inter'] text-base sm:text-lg leading-[1.8] text-[hsl(var(--muted-foreground))] mb-12"
            >
              {sections.intro}
            </motion.p>

            {/* Founder section */}
            <motion.div variants={fadeUp} className="mb-12">
              <div className="rounded-xl border border-[rgba(201,168,76,0.15)] p-8 sm:p-10 bg-[hsl(var(--card))]">
                <p className="font-['Inter'] text-xs font-medium uppercase tracking-[0.14em] text-[hsl(var(--rose))] mb-4">
                  {founder.title || 'Founder & Architect'}
                </p>
                <h2 className="font-['Cormorant_Garamond'] text-3xl font-bold text-[hsl(var(--foreground))] mb-4">
                  {founder.name || 'Emma'}
                </h2>
                <blockquote className="font-['Cormorant_Garamond'] text-xl sm:text-2xl italic leading-[1.4] text-[hsl(var(--foreground))] mb-6 border-l-2 border-[hsl(350,33%,44%)] pl-6">
                  {founder.bio}
                </blockquote>
                {founder.extended_bio && (
                  <div className="font-['Inter'] text-[15px] leading-[1.8] text-[hsl(var(--muted-foreground))] space-y-4">
                    {founder.extended_bio.split('\n\n').map((para, i) => (
                      <p key={para.slice(0,30)}>{para}</p>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>

            {/* Origin Story */}
            {sections.origin_story && (
              <motion.div variants={fadeUp}>
                <p className="font-['Inter'] text-xs font-medium uppercase tracking-[0.14em] text-[hsl(var(--silver))] mb-6">
                  Origin
                </p>
                <p className="font-['Inter'] text-[15px] leading-[1.8] text-[hsl(var(--muted-foreground))]">
                  {sections.origin_story}
                </p>
              </motion.div>
            )}
          </motion.div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
